import React, { useMemo } from 'react';

import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { useJiraContext } from 'src/services/jira';
import { useLinkedIncident } from 'src/services/linkedIncident';
import { useSession } from 'src/services/session';

import integration from 'src/lib/api/integration';
import pd from 'src/lib/api/pagerduty';

import Collapse from 'src/views/components/Collapse';
import ErrorMessage from 'src/views/components/ErrorMessage';

import './Timeline.css';

const logEntryComponents = {
  notify_log_entry: ({ user, channel, summary }) => {
    if (user && channel && channel.notification) {
      return (
        <>
          <span style={{ color: 'green' }}>Notified </span>
          <a href={user.html_url} target="_blank" rel="noopener noreferrer">{user.summary}</a> by {channel.notification.type}
        </>
      );
    }
    return summary;
  },

  assign_log_entry: ({ assignees, summary }) => {
    if (assignees && assignees.length) {
      return (
        <>
          <span style={{ color: '#b36200' }}>Assigned </span> to {assignees.map(
            (u, i) => (
              <span key={i}>
                <a href={u.html_url} target="_blank" rel="noopener noreferrer">{u.summary}</a>
                { i === assignees.length - 1 ? '' : ', '}
              </span>
            )
          )}
        </>
      );
    }
    return summary;
  },

  acknowledge_log_entry: ({ summary, agent }) => {
    if (agent && agent.summary && agent.html_url) {
      return (
        <>
          Acknowledged by <a href={agent.html_url} target="_blank" rel="noopener noreferrer">{agent.summary}</a>
        </>
      );
    }
    return summary;
  },

  resolve_log_entry: ({ summary }) => summary,
  trigger_log_entry: ({ summary }) => summary,

  unacknowledge_log_entry: ({ channel, agent, summary }) => {
    if (channel && channel.type === 'timeout') {
      return (
        <>
          <span style={{ color: token('color.text.danger', 'red') }}>Unacknowledged </span>by timeout
        </>
      );
    }

    if (channel && channel.type === 'user') {
      return (
        <>
          <span style={{ color: token('color.text.danger', 'red') }}>Unacknowledged </span>by <a href={agent.html_url} target="_blank" rel="noopener noreferrer">{agent.summary}</a>
        </>
      );
    }

    return summary;
  }
};

const DefaultLogEntryBody = ({ summary }) => summary;

const LogEntryBody = ({ type, ...rest }) => {
  const Component = logEntryComponents[type] || DefaultLogEntryBody;
  return <Component {...{ type, ...rest }}/>;
};

const LogEntry = ({ logEntry }) => {
  const date = new Date(Date.parse(logEntry.created_at));
  return (
    <div className={`log-entry ${logEntry.type}`} data-cy="log-entry" style={{ marginTop: '0.25rem' }}>
      <span style={{ fontStyle: 'oblique', marginRight: '0.25rem' }}>{date.toLocaleTimeString()}:</span>
      <LogEntryBody {...logEntry}/>
    </div>
  );
};

const GroupedEntry = ({ groupedEntry }) => (
  <div>
    <div data-cy="log-entry-group" style={{ marginTop: '1rem' }}>
      <span style={{ fontStyle: 'normal' }}>{groupedEntry.date}</span>
      {groupedEntry.entries.map(entry => <LogEntry key={entry.id} logEntry={entry} />)}
    </div>
  </div>
);

class Timeline extends React.Component {
  state = {
    loading: true,
    timeline: undefined,
    error: undefined,
    info: undefined,
  };

  groupByDate(logEntries) {
    const groupedTimeline = [];
    let timelineForDate = { date: null, entries: [] };

    logEntries.forEach((entry) => {
      const date = new Date(Date.parse(entry.created_at));
      const dateString = date.toLocaleDateString();

      if (timelineForDate.date === null) {
        timelineForDate = { date: dateString, entries: [entry] };
      } else if (timelineForDate.date === dateString) {
        timelineForDate.entries.push(entry);
      } else {
        groupedTimeline.push(timelineForDate);
        timelineForDate = { date: dateString, entries: [entry] };
      }
    });
    if (timelineForDate.date !== null) {
      groupedTimeline.push(timelineForDate);
    }
    return groupedTimeline;
  }

  componentDidMount() {
    const {
      jira: {
        settings: { pdApiKey: { value: pdApiKey } }
      },
      session: {
        loggedIn
      },
      incident: { id: incidentId },
      incidentAccountDomain,
      incidentByService,
    } = this.props;

    if (!loggedIn && incidentByService) {
      integration.getIncidentLogEntries(incidentId)
        .then(timeline => this.setState({ loading: false, timeline, error: undefined }))
        .catch(error => this.setState({ loading: false, error: ['Can not load incident Timeline', error] }));
    } else if (!loggedIn && !pdApiKey) {
      this.setState({
        loading: false,
        info: ['Cannot load Timeline', 'Please login or configure PagerDuty REST API Key on the PagerDuty Add-on settings page']
      });
    } else {
      pd.getIncidentLogEntries(incidentId, incidentAccountDomain)
        .then(timeline => this.setState({ loading: false, timeline, error: undefined }))
        .catch(error => this.setState({ loading: false, error: ['Can not load incident Timeline', error] }));
    }
  }

  componentWillUnmount() {
    this.setState = () => {};
  }

  render() {
    const { loading, error, info, timeline } = this.state;
    const logEntries = (timeline && timeline.log_entries) || [];
    const groupedEntries = this.groupByDate(logEntries);

    if (error) {
      return <ErrorMessage error={error}/>;
    }

    if (info) {
      return <ErrorMessage error={info} appearance="info"/>;
    }

    if (loading) {
      return <div><Spinner/></div>;
    }

    return (
      <div style={{ maxHeight: 200, overflow: 'auto' }} data-cy="timeline-content">
        {groupedEntries.map(groupedEntry => <GroupedEntry key={groupedEntry.date} groupedEntry={groupedEntry} />)}
      </div>
    );
  }
}

const TimeLineContainer = ({ incident, incidentAccountDomain }) => {
  const session = useSession();
  const jira = useJiraContext();
  const { byService: { incident: incidentByService } } = useLinkedIncident();

  const WithIncident = useMemo(() => () => <Timeline incident={incident} incidentAccountDomain={incidentAccountDomain} incidentByService={Boolean(incidentByService)} session={session} jira={jira}/>, [incident, incidentAccountDomain, incidentByService, jira, session]);

  return (
    <div className="timeline" data-cy="timeline-trigger" style={{ marginTop: '1rem' }}>
      <Collapse title="Timeline"
        persistenceKey="integration_jira_collapse_timeline"
        trackEventKey="timeline"
        component={WithIncident}/>
    </div>
  );
};

export default TimeLineContainer;
